import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'

import { listAppVersions, type AppPlatform, type UserAppVersionRow } from '@/api/app_versions'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { formatPhoneDisplay } from '@/utils/phone'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * /admin/notifications/app-versions — какая версия мобилы у каждого юзера.
 *
 * Этап 1 механизма «Обновление приложения»: только наблюдение. Блокеры
 * (`min_supported_build`, экран принудительного обновления) — отдельным шагом.
 *
 * Источник данных: RPC `admin_list_app_versions` (см. миграцию
 * `supabase/migrations/20260511120000_app_version_reporting.sql`).
 * Мобила пишет свою версию через `report_app_version` при старте и при
 * логине; web-админка сюда не пишет — у неё нет своей мобильной сборки.
 */
export function AppVersionsPage() {
  const { t } = useTranslation()
  const [platformFilter, setPlatformFilter] = useState<'all' | AppPlatform | 'unknown'>('all')
  const [reportedFilter, setReportedFilter] = useState<'all' | 'reported' | 'unknown'>('all')

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-app-versions'],
    queryFn: listAppVersions,
    staleTime: 30_000,
  })

  const rows = data ?? []

  const stats = useMemo(() => {
    const total = rows.length
    const reported = rows.filter((r) => r.app_build != null).length
    let latestBuild = 0
    let latestVersion: string | null = null
    for (const r of rows) {
      if (r.app_build != null && r.app_build > latestBuild) {
        latestBuild = r.app_build
        latestVersion = r.app_version
      }
    }
    const onLatest = rows.filter((r) => r.app_build === latestBuild && latestBuild > 0).length
    return { total, reported, latestBuild, latestVersion, onLatest }
  }, [rows])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (reportedFilter === 'reported' && r.app_build == null) return false
      if (reportedFilter === 'unknown' && r.app_build != null) return false
      if (platformFilter === 'all') return true
      if (platformFilter === 'unknown') return r.app_platform == null
      return r.app_platform === platformFilter
    })
  }, [rows, platformFilter, reportedFilter])

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('notif.appVersions.title')}
        subtitle={t('notif.appVersions.subtitle')}
      />

      {/* Стат-блок */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label={t('notif.appVersions.totalUsers')} value={String(stats.total)} />
        <StatCard
          label={t('notif.appVersions.reportedVersion')}
          value={`${stats.reported} / ${stats.total}`}
          hint={stats.total > 0 ? `${Math.round((stats.reported / stats.total) * 100)}%` : undefined}
        />
        <StatCard
          label={t('notif.appVersions.latestReported')}
          value={
            stats.latestVersion
              ? `${stats.latestVersion} (build ${stats.latestBuild})`
              : '—'
          }
        />
        <StatCard
          label={t('notif.appVersions.onLatest')}
          value={
            stats.latestBuild > 0
              ? `${stats.onLatest} / ${stats.reported}`
              : '—'
          }
          hint={
            stats.latestBuild > 0 && stats.reported > 0
              ? t('notif.appVersions.percentOfReported', {
                  percent: Math.round((stats.onLatest / stats.reported) * 100),
                })
              : undefined
          }
        />
      </div>

      {/* Фильтры */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--stryzh-color-border-default)] p-3">
        <FilterSelect
          label={t('notif.appVersions.platform')}
          value={platformFilter}
          onChange={(v) => setPlatformFilter(v as typeof platformFilter)}
          options={[
            { value: 'all', label: t('notif.appVersions.platformAll') },
            { value: 'ios', label: 'iOS' },
            { value: 'android', label: 'Android' },
            { value: 'web', label: 'Web' },
            { value: 'unknown', label: t('notif.appVersions.platformUnknown') },
          ]}
        />
        <FilterSelect
          label={t('notif.appVersions.reportStatus')}
          value={reportedFilter}
          onChange={(v) => setReportedFilter(v as typeof reportedFilter)}
          options={[
            { value: 'all', label: t('notif.appVersions.platformAll') },
            { value: 'reported', label: t('notif.appVersions.hasData') },
            { value: 'unknown', label: t('notif.appVersions.noData') },
          ]}
        />
        <button
          type="button"
          onClick={() => void refetch()}
          className="ml-auto rounded-md border border-[var(--stryzh-color-border-default)] px-3 py-1.5 text-sm hover:bg-[var(--stryzh-color-border-subtle)]"
          disabled={isFetching}
        >
          {isFetching ? t('notif.appVersions.refreshing') : t('notif.appVersions.refresh')}
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-200">
          {t('notif.appVersions.error', { message: (error as Error).message })}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        <table className="w-full min-w-[960px] border-collapse text-left text-sm">
          <thead className="sticky top-0 z-10 bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2 font-medium">{t('notif.appVersions.colUser')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.appVersions.colPhone')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.appVersions.colRole')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.appVersions.colPlatform')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.appVersions.colVersion')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.appVersions.colBuild')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.appVersions.colLastReport')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center">
                  <Loader size="sm" centered label={t('common.loading')} />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-[var(--stryzh-color-text-secondary)]">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              filtered.map((r) => <UserRow key={r.user_id} row={r} latestBuild={stats.latestBuild} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function UserRow({
  row,
  latestBuild,
}: {
  row: UserAppVersionRow
  latestBuild: number
}) {
  const isOnLatest = row.app_build != null && row.app_build === latestBuild && latestBuild > 0
  const isOutdated = row.app_build != null && latestBuild > 0 && row.app_build < latestBuild
  return (
    <tr className="border-t border-[var(--stryzh-color-border-default)] hover:bg-[var(--stryzh-color-border-subtle)]">
      <td className="px-3 py-2">{row.full_name || '—'}</td>
      <td className="px-3 py-2 font-mono text-xs">{row.phone ? formatPhoneDisplay(row.phone) : '—'}</td>
      <td className="px-3 py-2 text-[var(--stryzh-color-text-secondary)]">{row.role || '—'}</td>
      <td className="px-3 py-2">{row.app_platform ?? '—'}</td>
      <td className="px-3 py-2 font-mono text-xs">{row.app_version || '—'}</td>
      <td
        className={
          isOutdated
            ? 'px-3 py-2 font-mono text-xs text-amber-400'
            : isOnLatest
              ? 'px-3 py-2 font-mono text-xs text-emerald-400'
              : 'px-3 py-2 font-mono text-xs'
        }
      >
        {row.app_build ?? '—'}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-[var(--stryzh-color-text-secondary)]">
        {row.app_version_updated_at
          ? `${formatDistanceToNow(new Date(row.app_version_updated_at), { locale: dateFnsLocale(), addSuffix: true })}`
          : '—'}
      </td>
    </tr>
  )
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-[var(--stryzh-color-border-default)] p-3">
      <div className="text-xs text-[var(--stryzh-color-text-secondary)]">{label}</div>
      <div className="mt-1 text-base font-medium">{value}</div>
      {hint ? <div className="mt-1 text-xs text-[var(--stryzh-color-text-secondary)]">{hint}</div> : null}
    </div>
  )
}

function FilterSelect<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: T
  onChange: (v: T) => void
  options: ReadonlyArray<{ value: T; label: string }>
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-[var(--stryzh-color-text-secondary)]">{label}:</span>
      <select
        className="rounded-md border border-[var(--stryzh-color-border-default)] bg-transparent px-2 py-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-[var(--stryzh-color-surface)]">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}
